import { useEffect } from 'react';
import { UseFormReturn, Path, PathValue, FieldValues } from 'react-hook-form';

type FieldSync<T extends FieldValues> = {
  [K in Path<T>]?: (value: PathValue<T, K>) => Partial<T>;
};

interface FieldSyncConfig<T extends FieldValues> {
  form: UseFormReturn<T>;
  fields: FieldSync<T>;
  enabled?: boolean;
}

export const useFieldSync = <T extends FieldValues>({ form, fields, enabled = true }: FieldSyncConfig<T>) => {
  const { watch, setValue } = form;

  useEffect(() => {
    if (!enabled) return;

    const subscription = watch((formState, { name, type }) => {
      if (type !== 'change' || !name) return;

      const calculate = fields[name];
      if (!calculate || !formState[name]) return;

      const updates = calculate(formState[name]);
      (Object.entries(updates) as [Path<T>, PathValue<T, Path<T>>][]).forEach(([field, newValue]) => {
        setValue(field, newValue, { shouldValidate: true });
      });
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue, fields, enabled]);
};
